import {
  TypedUseSelectorHook,
  useDispatch as _useDispatch,
  useSelector as _useSelector,
} from 'react-redux';
import isEqual from 'lodash/isEqual';
import type { AppDispatch, RootState } from '@/store/store';
import { buildCompanyMemberKey } from '@/store/slices/companyMembersSlice';
import { buildCompanyInvestorKey } from '@/store/slices/companyInvestorsSlice';
import {
  buildAllCompanies,
  buildAllCompanyPosts,
  buildAllInvestors,
  buildAllNotifications,
  buildAllUsers,
  buildCompaniesFromIdArray,
  buildCompany,
  buildCompanyFiles,
  buildCompanyFinancialReports,
  buildCompanyInvestor,
  buildCompanyInvestors,
  buildCompanyMember,
  buildCompanyMembers,
  buildCompanyPosts,
  buildCurrentInvestor,
  buildCurrentProfile,
  buildCurrentProfilePreferences,
  buildCurrentProfileProperties,
  buildCurrentUser,
  buildDateFormatPreference,
  buildFile,
  buildFilesFromIdArray,
  buildFinancialReport,
  buildHasCompanyAccess,
  buildInvestment,
  buildInvestor,
  buildInvestorCompanies,
  buildInvestorCompany,
  buildLatestFinancialReport,
  buildLatestFinancialReportsFromIdArray,
  buildMemberCompanies,
  buildNotification,
  buildNotificationsFromIdArray,
  buildPost,
  buildPostFiles,
  buildProfile,
  buildUnreadNotificationsCount,
  buildUser,
  buildWeekStartsOnPreference,
} from './utils';

export const useDispatch: () => AppDispatch = _useDispatch;
export const useSelector: TypedUseSelectorHook<RootState> = _useSelector;

function useBuilder(
  buildMethod: (state: RootState, identifier?: any) => any,
  identifier?: any
) {
  if (identifier !== undefined) {
    return useSelector((state) => buildMethod(state, identifier), isEqual);
  } else {
    return useSelector((state) => buildMethod(state), isEqual);
  }
}

export function useCurrentUser(): ReturnType<typeof buildCurrentUser> {
  return useBuilder(buildCurrentUser);
}

export function useCurrentProfile(): ReturnType<typeof buildCurrentProfile> {
  return useBuilder(buildCurrentProfile);
}

export function useCurrentProfilePreferences(): ReturnType<
  typeof buildCurrentProfilePreferences
> {
  return useBuilder(buildCurrentProfilePreferences);
}

export function useCurrentProfileProperties(): ReturnType<
  typeof buildCurrentProfileProperties
> {
  return useBuilder(buildCurrentProfileProperties);
}

export function useDateFormatPreference(): ReturnType<
  typeof buildDateFormatPreference
> {
  return useBuilder(buildDateFormatPreference);
}

export function useProfile(
  profileId?: number
): ReturnType<typeof buildProfile> {
  return useBuilder(buildProfile);
}

export function useAllCompanies(): ReturnType<typeof buildAllCompanies> {
  return useBuilder(buildAllCompanies);
}

export function useCompany(
  companyId?: number
): ReturnType<typeof buildCompany> {
  return useBuilder(buildCompany, companyId);
}

export function useCompaniesFromIdArray(
  idArray: number[]
): ReturnType<typeof buildCompaniesFromIdArray> {
  return useBuilder(buildCompaniesFromIdArray, idArray);
}

export function useCompanyFiles(
  companyId?: number
): ReturnType<typeof buildCompanyFiles> {
  return useBuilder(buildCompanyFiles, companyId);
}

export function useFile(fileId: number): ReturnType<typeof buildFile> {
  return useBuilder(buildFile, fileId);
}

export function useFilesFromIdArray(
  idArray: number[]
): ReturnType<typeof buildFilesFromIdArray> {
  return useBuilder(buildFilesFromIdArray, idArray);
}

export function useUser(userId: number): ReturnType<typeof buildUser> {
  return useBuilder(buildUser, userId);
}

export function useAllUsers(): ReturnType<typeof buildAllUsers> {
  return useBuilder(buildAllUsers);
}

export function useAllInvestors(): ReturnType<typeof buildAllInvestors> {
  return useBuilder(buildAllInvestors);
}

export function useInvestor(
  investorId?: number
): ReturnType<typeof buildInvestor> {
  return useBuilder(buildInvestor, investorId);
}

export function useHasCompanyAccess(
  companyId?: number
): ReturnType<typeof buildHasCompanyAccess> {
  return useBuilder(buildHasCompanyAccess, companyId);
}

export function useCompanyMember(
  companyId: number,
  profileId: number
): ReturnType<typeof buildCompanyMember> {
  return useBuilder(
    buildCompanyMember,
    buildCompanyMemberKey(companyId, profileId)
  );
}

export function useCompanyPosts(
  companyId?: number
): ReturnType<typeof buildCompanyPosts> {
  return useBuilder(buildCompanyPosts, companyId);
}

export function useAllCompanyPosts(): ReturnType<typeof buildAllCompanyPosts> {
  return useBuilder(buildAllCompanyPosts);
}

export function usePost(postId?: number): ReturnType<typeof buildPost> {
  return useBuilder(buildPost, postId);
}

export function usePostFiles(
  postId?: number
): ReturnType<typeof buildPostFiles> {
  return useBuilder(buildPostFiles, postId);
}

export function useCompanyMembers(
  companyId?: number
): ReturnType<typeof buildCompanyMembers> {
  return useBuilder(buildCompanyMembers, companyId);
}

export function useCompanyInvestor(
  companyId: number,
  investorId: number
): ReturnType<typeof buildCompanyInvestor> {
  return useBuilder(
    buildCompanyInvestor,
    buildCompanyInvestorKey(companyId, investorId)
  );
}

export function useCompanyInvestors(
  companyId?: number
): ReturnType<typeof buildCompanyInvestors> {
  return useBuilder(buildCompanyInvestors, companyId);
}

export function useCurrentInvestor(): ReturnType<typeof buildCurrentInvestor> {
  return useBuilder(buildCurrentInvestor);
}

export function useMemberCompanies(
  profileId?: number
): ReturnType<typeof buildMemberCompanies> {
  return useBuilder(buildMemberCompanies, profileId);
}

export function useInvestorCompany(
  companyId: number,
  investorId: number
): ReturnType<typeof buildInvestorCompany> {
  return useBuilder(
    buildInvestorCompany,
    buildCompanyInvestorKey(companyId, investorId)
  );
}

export function useInvestorCompanies(
  investorId?: number
): ReturnType<typeof buildInvestorCompanies> {
  return useBuilder(buildInvestorCompanies, investorId);
}

export function useInvestment(
  investmentId?: number
): ReturnType<typeof buildInvestment> {
  return useBuilder(buildInvestment, investmentId);
}

export function useWeekStartsOnPreference(): ReturnType<
  typeof buildWeekStartsOnPreference
> {
  return useBuilder(buildWeekStartsOnPreference);
}

export function useCompanyFinancialReports(
  companyId?: number
): ReturnType<typeof buildCompanyFinancialReports> {
  return useBuilder(buildCompanyFinancialReports, companyId);
}

export function useFinancialReport(
  reportId?: number
): ReturnType<typeof buildFinancialReport> {
  return useBuilder(buildFinancialReport, reportId);
}

export function useLatestFinancialReport(
  companyId?: number
): ReturnType<typeof buildLatestFinancialReport> {
  return useBuilder(buildLatestFinancialReport, companyId);
}

export function useLatestFinancialReportsFromIdArray(
  companyIds?: number[]
): ReturnType<typeof buildLatestFinancialReportsFromIdArray> {
  return useBuilder(buildLatestFinancialReportsFromIdArray, companyIds);
}

export function useNotification(
  notificationId: number
): ReturnType<typeof buildNotification> {
  return useBuilder(buildNotification, notificationId);
}

export function useAllNotifications(): ReturnType<
  typeof buildAllNotifications
> {
  return useBuilder(buildAllNotifications);
}

export function useNotificationsFromIdArray(
  idArray: number[]
): ReturnType<typeof buildNotificationsFromIdArray> {
  return useBuilder(buildNotificationsFromIdArray, idArray);
}

export function useUnreadNotificationsCount(): ReturnType<
  typeof buildUnreadNotificationsCount
> {
  return useBuilder(buildUnreadNotificationsCount);
}
